import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { taggedFetch } from '../services/taggedFetch';

const OAuthCallbackPage: React.FC = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
    const [errorMessage, setErrorMessage] = useState<string>('');

    useEffect(() => {
        const handleCallback = async () => {
            // Get code and state from URL parameters
            const code = searchParams.get('code');
            const state = searchParams.get('state');
            const error = searchParams.get('error');

            // Determine provider and redirect target from URL path
            const path = window.location.pathname;
            let provider = 'google-drive';
            let redirectTo = '/connections';

            if (path.includes('google-drive')) {
                provider = 'google-drive';
                redirectTo = '/connections';
            } else if (path.includes('gmail')) {
                provider = 'gmail';
                redirectTo = '/integrations';
            } else if (path.includes('google')) {
                provider = 'google';
                redirectTo = '/connections';
            } else if (path.includes('microsoft')) {
                provider = 'microsoft';
                redirectTo = '/integrations'; // Microsoft email goes to integrations
            }

            // Check for OAuth errors
            if (error) {
                setStatus('error');
                setErrorMessage(`OAuth error: ${error}`);
                setTimeout(() => navigate(redirectTo), 3000);
                return;
            }

            if (!code || !state) {
                setStatus('error');
                setErrorMessage('Missing authorization code or state');
                setTimeout(() => navigate(redirectTo), 3000);
                return;
            }

            try {
                // Exchange authorization code for tokens
                const response = await taggedFetch('/api/oauth/callback', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                        code,
                        state,
                        provider
                    }),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.detail || 'Failed to complete OAuth');
                }

                const data = await response.json();
                console.log('OAuth callback successful:', data);

                setStatus('success');

                // Redirect to appropriate page after 2 seconds
                setTimeout(() => {
                    navigate(redirectTo);
                }, 2000);

            } catch (error) {
                console.error('OAuth callback error:', error);
                setStatus('error');
                setErrorMessage(error instanceof Error ? error.message : 'Unknown error');
                setTimeout(() => navigate(redirectTo), 3000);
            }
        };

        handleCallback();
    }, [searchParams, navigate]);

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '2rem'
        }}>
            {status === 'processing' && (
                <>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
                    <h2>{t('connections.processing')}</h2>
                    <p>{t('connections.pleaseWait')}</p>
                </>
            )}

            {status === 'success' && (
                <>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem', color: '#10b981' }}>✅</div>
                    <h2>{t('connections.connectionSuccess')}</h2>
                    <p>{t('connections.redirecting')}</p>
                </>
            )}

            {status === 'error' && (
                <>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem', color: '#ef4444' }}>❌</div>
                    <h2>{t('connections.connectionFailed')}</h2>
                    <p style={{ color: '#ef4444', marginBottom: '1rem' }}>{errorMessage}</p>
                    <p>{t('connections.redirecting')}</p>
                </>
            )}
        </div>
    );
};

export default OAuthCallbackPage;
